import React, { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ClotOption {
  value: string;
  label: string;
  dotSizePx?: number;
  description?: string;
  risk?: "normal" | "monitor" | "alert";
}

interface ClotTrackPickerProps {
  options: ClotOption[];
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const RISK_COLORS: Record<string, string> = {
  normal: "hsl(173, 52%, 24%)",
  monitor: "hsl(36, 80%, 50%)",
  alert: "hsl(0, 65%, 48%)",
};

export default function ClotTrackPicker({
  options,
  value,
  onChange,
  disabled = false,
}: ClotTrackPickerProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const activeIndex = options.findIndex((o) => o.value === value);
  const stopCount = options.length;
  const getPercent = (i: number) => (i / (stopCount - 1)) * 100;

  const activeOpt = value ? options.find((o) => o.value === value) : undefined;

  const alertText = (() => {
    if (!activeOpt) return null;
    if (activeOpt.value === "large")
      return "Large clots can be a sign you need medical advice.";
    if (activeOpt.value === "frequent")
      return "Frequent clots can be a sign you need medical advice.";
    if (activeOpt.risk === "alert")
      return "This option may need medical advice.";
    return null;
  })();

  const accentColor = activeOpt
    ? RISK_COLORS[activeOpt.risk ?? "normal"]
    : RISK_COLORS.normal;

  return (
    <div className="rounded-2xl bg-[#F5F5F5] p-5">
      <div className="relative pt-10 pb-2 px-2" ref={trackRef}>
        <div className="absolute left-2 right-2 top-[calc(50%+4px)] h-[3px] rounded-full bg-black/10" />

        {activeIndex >= 0 && (
          <motion.div
            className="absolute top-[calc(50%+4px)] left-2 h-[3px] rounded-full"
            style={{ backgroundColor: accentColor }}
            initial={false}
            animate={{ width: `${getPercent(activeIndex)}%` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />
        )}

        <div className="relative flex justify-between items-center">
          {options.map((opt, i) => {
            const isActive = activeIndex === i;
            const isPast = activeIndex >= i && activeIndex >= 0;
            const dotSize = opt.dotSizePx ?? 0;
            const riskColor = RISK_COLORS[opt.risk ?? "normal"];

            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => onChange(opt.value)}
                className="relative flex flex-col items-center gap-0 disabled:opacity-40 group z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1e5e5b] rounded-lg"
                style={{ width: `${100 / stopCount}%` }}
              >
                <div className="h-10 flex items-end justify-center mb-1">
                  {dotSize > 0 && (
                    <motion.span
                      className="rounded-full block shadow-sm"
                      initial={false}
                      animate={{
                        backgroundColor: isActive ? riskColor : "hsl(0, 0%, 78%)",
                        width: dotSize,
                        height: dotSize,
                        scale: isActive ? 1.2 : 1,
                      }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    />
                  )}
                </div>

                <motion.div
                  className="rounded-full border-2 shadow-sm"
                  initial={false}
                  animate={{
                    width: isActive ? 22 : 14,
                    height: isActive ? 22 : 14,
                    backgroundColor: isPast ? accentColor : "#fff",
                    borderColor: isPast ? accentColor : "hsl(0, 0%, 78%)",
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                />

                <span
                  className={[
                    "mt-2 text-xs text-center leading-tight transition-colors duration-150",
                    isActive
                      ? "font-bold text-[#0c2624]"
                      : "font-medium text-[#6b7280]",
                  ].join(" ")}
                >
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeOpt?.description && (
          <motion.div
            key={activeOpt.value}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className="mt-4 pt-3 border-t border-black/5"
          >
            <p className="text-xs text-[#6b7280] leading-relaxed">
              {activeOpt.description}
            </p>

            {alertText && (
              <p className="mt-2 text-xs font-semibold text-red-600 leading-relaxed">
                {alertText}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-4 pt-3 border-t border-black/5">
        <p className="text-[11px] text-[#6b7280] leading-relaxed">
          Medium clots are about the size of a 50p coin. If clots are larger or
          frequent, please contact your midwife.
        </p>
      </div>
    </div>
  );
}